import { Button } from '@monorepo/shared/componentsV2/Button';
import TrashIcon from '@svg/trash.svg';
import { FieldResponse, NullableDate } from 'mapistry-shared';
import React from 'react';
import styled from 'styled-components';
import { cardBorder } from '../../../styles/card';
import {
  compose,
  isRequired as isRequiredValidator,
  makeIsAfterDate,
} from '../../../utils/validators';
import { DatePicker } from '../../dateAndTimePickers';
import { EditFieldValueFactory } from '../../fieldDataType/values/EditFieldValueFactory/EditFieldValueFactory';
import { DatePickerField } from '../../fields/DatePickerField';
import { WysiwygEditorField } from '../../fields/WysiwygEditorField';
import { StyledFormGroup } from '../../styled/form';

const wysiwygEditorModules = {
  toolbar: [['bold', 'italic', 'underline', 'strike'], ['link']],
};
const wysiwygEditorFormats = ['bold', 'italic', 'underline', 'strike', 'link'];

const CardContainer = styled.div`
  padding: 1rem;
  margin-bottom: 1.25rem;
  background-color: ${({ theme }) => theme.colors.grayf6};
  ${cardBorder}
`;

const EffectiveDatesFormGroup = styled(StyledFormGroup)`
  align-items: center;
`;

const ActiveValueLabel = styled.div`
  font-style: italic;
`;

const DeleteButton = styled(Button)`
  margin-top: 1rem;
`;

type EffectiveDatesProps = {
  endDate?: NullableDate; // not-editable, equals start date of the next version
  formFieldName: string;
  isFirstInHistory?: boolean;
  isLastInHistory?: boolean;
  isReadOnly?: boolean;
  previousStartDate?: NullableDate;
};

function EffectiveDateRange({
  endDate,
  formFieldName,
  isFirstInHistory,
  isLastInHistory,
  isReadOnly,
  previousStartDate,
}: EffectiveDatesProps) {
  const styledActiveValueLabel = (
    <ActiveValueLabel>Active value</ActiveValueLabel>
  );

  if (isFirstInHistory && isLastInHistory) {
    return styledActiveValueLabel;
  }

  return (
    <EffectiveDatesFormGroup row>
      {!isFirstInHistory && (
        <DatePickerField
          disabled={isReadOnly}
          label="Start date"
          name={formFieldName}
          required
          validate={compose(
            isRequiredValidator,
            makeIsAfterDate(previousStartDate),
          )}
        />
      )}
      {!isLastInHistory ? (
        <DatePicker
          disabled
          label="End date"
          name={`${formFieldName}-end-display`}
          onChange={() => undefined}
          value={endDate || null}
        />
      ) : (
        styledActiveValueLabel
      )}
    </EffectiveDatesFormGroup>
  );
}

type HistoricalValueCardProps = {
  additionalContent?: React.ReactNode;
  // the derived end date (next version's begin) shown read only
  endDate?: NullableDate;
  field: FieldResponse;
  // the Final Form path for this version (e.g. `${fieldId}[0]`)
  formFieldName: string;
  isFirstInHistory?: boolean;
  isLastInHistory?: boolean;
  isReadOnly?: boolean;
  onDelete?: () => void;
  organizationId: string;
  previousStartDate?: NullableDate;
  projectId: string | undefined;
};

export function HistoricalValueCard({
  additionalContent = null,
  endDate,
  field,
  formFieldName,
  isFirstInHistory,
  isLastInHistory,
  isReadOnly,
  onDelete,
  organizationId,
  previousStartDate,
  projectId,
}: HistoricalValueCardProps) {
  const notesFieldName = `${formFieldName}.notes`;

  return (
    <CardContainer>
      <StyledFormGroup row>
        <EditFieldValueFactory
          field={field}
          nameOverride={`${formFieldName}.value`}
          organizationId={organizationId}
          projectId={projectId}
          isReadOnly={isReadOnly}
        />
      </StyledFormGroup>
      <EffectiveDateRange
        endDate={endDate}
        formFieldName={`${formFieldName}.begin`}
        isFirstInHistory={isFirstInHistory}
        isLastInHistory={isLastInHistory}
        isReadOnly={isReadOnly}
        previousStartDate={previousStartDate}
      />
      <WysiwygEditorField
        dontSaveSpaceForErrorText
        label="Reference"
        modules={wysiwygEditorModules}
        formats={wysiwygEditorFormats}
        name={notesFieldName}
      />
      {onDelete && (
        <DeleteButton color="secondary" onClick={onDelete}>
          <TrashIcon />
          Delete
        </DeleteButton>
      )}
      {additionalContent}
    </CardContainer>
  );
}
